import { filter, set } from 'lodash';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box, // Import Box from @mui/material
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import { Title } from '@mui/icons-material';
// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import SearchNotFound from '../components/SearchNotFound';

import { GalleryListToolbar, GalleryListHead, GalleryMoreMenu } from '../sections/reports';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
  { id: 'month', label: 'Month', alignRight: false },
  { id: 'year', label: 'Year', alignRight: false },
  { id: 'topic', label: 'Topic', alignRight: false },
  { id: 'images', label: 'Images', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, roleFilter) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let filteredArray = stabilizedThis.map((el) => el[0]);

  // Apply text query filter
  if (query) {
    filteredArray = filteredArray.filter((poster) => {
      if (poster.title.toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      if (poster.images.toLowerCase().includes(query.toLowerCase())) { // This needs to be adjusted to handle array of images
        return true;
      }
      return false;
    });
  }

  return filteredArray;
}

export default function Gallery() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('email'); // Default sort by email
  const [filterName, setFilterName] = useState('');
  const [roleFilter, setRoleFilter] = useState(''); // State for role filter
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // State for gallery
  const [gallery, setGallery] = useState([]);
  const [editinggaleryId, setEditingGalleryId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [month, setMonth] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [year, setYear] = useState('');
  const [topic, setTopic] = useState('');
  const [images, setImages] = useState([]); // Changed to an array
  const [newImageLink, setNewImageLink] = useState(''); // New state for the input field
  const [selectedgallery, setSelectedGallery] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Fetch gallery from the server on component mount
  useEffect(() => {
    fetchgallery();
  }, []);

  // Fetch gallery from the server
  const fetchgallery = async () => {
    try {
      const response = await axios.get('https://cpccbe.org/api/gallery/getGallery');

      setGallery(response.data.reverse()); // Reverse to show latest first
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = gallery.map((n) => n.email); // Use email as unique identifier
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, email) => {
    const selectedIndex = selected.indexOf(email);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, email);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleOpenModal = (galleryId) => {
    if (galleryId) {
      const gallerytoEdit = gallery.find((gallery) => gallery._id === galleryId);
      if (gallerytoEdit) {
        setTitle(gallerytoEdit.title);
        setDescription(gallerytoEdit.description);
        setMonth(gallerytoEdit.month);
        setYear(gallerytoEdit.year);
        setTopic(gallerytoEdit.topic);
        setImages(gallerytoEdit.images);

        setEditingGalleryId(galleryId);
      }
    } else {
      setTitle('');
      setDescription('');
      setMonth('');
      setYear('');
      setTopic('');
      setImages([]); // Initialize as empty array
      setEditingGalleryId(null);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setTitle('');
    setDescription('');
    setMonth('');
    setYear('');
    setTopic('');
    setImages([]); // Initialize as empty array
    setNewImageLink(''); // Clear the input field
    setEditingGalleryId(null);
    setOpenModal(false);
  };

  // Handle adding a new gallery
  const handleAddgallery = async () => {
    try {
      const response = await axios.post('https://cpccbe.org/api/gallery/createGallery', {
        month,
        year,
        topic,
        images, // Send the array of images
      });

      fetchgallery(); // Refresh gallery list after adding
      handleCloseModal(); // Close modal after adding
    } catch (error) {
      console.error('Error adding gallery:', error);
    }
  };

  // Handle updating a gallery
  const handleUpdategallery = async () => {
    try {
      await axios.patch(`https://cpccbe.org/api/gallery/updateGallery/${editinggaleryId}`, {
        title,
        description,
        month,
        year,
        topic,
        images, // Send the array of images
      });
      fetchgallery(); // Refresh gallery  list after updating
      handleCloseModal(); // Close modal after updating
    } catch (error) {
      console.error('Error updating gallery:', error);
    }
  };

  // Handle deleting gallery
  const handleDeletegallery = async (galleryId) => {
    if (window.confirm('Are you sure you want to delete this gallery?')) {
      try {
        await axios.delete(`https://cpccbe.org/api/gallery/deleteGallery/${galleryId}`); // Adjust endpoint as necessary

        fetchgallery(); // Refresh gallery list after deletion
      } catch (error) {
        console.error('Error deleting gallery:', error);
      }
    }
  };

  const handleViewgallery = (gallery) => {
    // Open a modal or set state to display gallery details
    setSelectedGallery(gallery); // Assuming you have state to hold selected consultancy data

    setViewModalOpen(true); // Open the modal for viewing poster details
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - gallery.length) : 0;

  // Apply sorting and filtering
  const filteredgallery = applySortFilter(gallery, getComparator(order, orderBy), filterName, roleFilter);

  const isgalleryNotFound = filteredgallery.length === 0;

  // Function to add a new image link to the array
  const handleAddImageLink = () => {
    if (newImageLink.trim() !== '') {
      setImages([...images, newImageLink]);
      setNewImageLink(''); // Clear the input field after adding
    }
  };

  // Function to remove an image link from the array
  const handleRemoveImageLink = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleImageLinkChange = (e) => {
    setNewImageLink(e.target.value);
  };

  return (
    <Page title="gallery">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Gallery
          </Typography>
          <Button variant="contained" onClick={handleOpenModal} startIcon={<Iconify icon="eva:plus-fill" />}>
            New Gallery
          </Button>
        </Stack>

        <Card>
          <GalleryListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            roleFilter={roleFilter}
            onRoleFilterChange={(e) => setRoleFilter(e.target.value)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <GalleryListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={gallery.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredgallery.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { _id, email, role } = row; // Adjust based on your actual data structure
                    const isItemSelected = selected.indexOf(email) !== -1;

                    return (
                      <TableRow
                        hover
                        key={_id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell align="left">
                          <IconButton onClick={() => handleViewgallery(row)} aria-label="view gallery">
                            <Iconify icon="eva:eye-fill" />
                          </IconButton>
                        </TableCell>
                        <TableCell align="left">{row.title}</TableCell>
                        <TableCell align="left">{row.description}</TableCell>
                        <TableCell align="left">{row.month}</TableCell>
                        <TableCell align="left">{row.year}</TableCell>
                        <TableCell align="left">{row.topic}</TableCell>
                        {/* <TableCell align="left" >{row.images}</TableCell> */} {/*  Modify as needed */}
                        <TableCell align="left" sx={{ whiteSpace: "normal" }}>
                          {Array.isArray(row.images) &&
                            row.images.map((link, index) => (
                              <a
                                key={index}
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "block", wordBreak: "break-word" }}
                              >
                                {link}
                              </a>
                            ))}
                        </TableCell>

                        <TableCell align="left">
                          <GalleryMoreMenu
                            onEdit={() => handleOpenModal(row._id)}
                            onDelete={() => handleDeletegallery(row._id)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isgalleryNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={gallery.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Modal for Editing gallery */}
        <Dialog open={openModal} onClose={handleCloseModal}>
          <DialogTitle>{editinggaleryId ? 'Edit gallery ' : 'Add New gallery'}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="title"
              label="Title"
              type="text"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              margin="dense"
              id="description"
              label="Description"
              type="text"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              margin="dense"
              id="month"
              label="Month"
              type="text"
              fullWidth
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
            <TextField
              margin="dense"
              id="year"
              label="year"
              type="text"
              fullWidth
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
            <TextField
              margin="dense"
              id="topic"
              label="topic"
              type="text"
              fullWidth
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />

            {/* Input for adding multiple image links */}
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Image Link"
                type="text"
                fullWidth
                value={newImageLink}
                onChange={handleImageLinkChange}
              />
              <Button variant="contained" sx={{ mt: 1 }} onClick={handleAddImageLink}>
                Add Image Link
              </Button>
            </Box>

            {/* Display added image links */}
            <Box sx={{ mt: 2 }}>
              {images.map((image, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography>{image}</Typography>
                  <IconButton onClick={() => handleRemoveImageLink(index)} aria-label="remove image">
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            {editinggaleryId ? (
              <Button onClick={handleUpdategallery}>Update gallery</Button>
            ) : (
              <Button onClick={handleAddgallery}>Add gallery</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
          <DialogTitle>Gallery Details</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="title"
              label="Title"
              type="text"
              fullWidth
              value={selectedgallery?.title}
              disabled
            />
            <TextField
              margin="dense"
              id="discription"
              label="Description"
              type="text"
              fullWidth
              value={selectedgallery?.description}
              disabled
            />
            
            <TextField
              margin="dense"
              id="month"
              label="Month"
              type="text"
              fullWidth
              value={selectedgallery?.month}
              disabled
            />
            <TextField
              margin="dense"
              id="year"
              label="Year"
              type="text"
              fullWidth
              value={selectedgallery?.year}
              disabled
            />
            <TextField
              margin="dense"
              id="topic"
              label="Topic"
              type="text"
              fullWidth
              value={selectedgallery?.topic}
              disabled
            />
            <Box> {/* Added Box component */}
              {selectedgallery?.images && Array.isArray(selectedgallery.images) ? (
                selectedgallery.images.map((image, index) => (
                  <Typography key={index}>{image}</Typography>
                ))
              ) : (
                <Typography>No images</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
